import { createApi } from 'utils/@reduxjs/toolkit';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import { useInjectReducer } from 'utils/redux-injectors';
import {
  AttentionItemsResponse,
  DashboardStatsResponse,
  RecentActivityResponse,
  AnalyticsMetricsResponse,
  TopProductsResponse,
} from './types';

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery,
  endpoints: builder => ({
    getDashboardStats: builder.query<DashboardStatsResponse, void>({
      query: () => ({
        url: endpoints.dashboard.stats.url,
        method: endpoints.dashboard.stats.method,
      }),
    }),
    getAttentionItems: builder.query<
      AttentionItemsResponse,
      { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }
    >({
      query: params => ({
        url: endpoints.dashboard.attentionItems.url,
        method: endpoints.dashboard.attentionItems.method,
        params,
      }),
    }),
    getRecentActivity: builder.query<
      RecentActivityResponse,
      {
        page?: number;
        limit?: number;
        actionType?: string;
        productId?: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: params => ({
        url: endpoints.dashboard.recentActivity.url,
        method: endpoints.dashboard.recentActivity.method,
        params,
      }),
    }),
    getAnalyticsMetrics: builder.query<
      AnalyticsMetricsResponse,
      {
        dateRange?: 'last7days' | 'last30days' | 'last90days' | 'custom';
        startDate?: string;
        endDate?: string;
      }
    >({
      query: params => ({
        url: endpoints.dashboard.analyticsMetrics.url,
        method: endpoints.dashboard.analyticsMetrics.method,
        params,
      }),
    }),
    getTopProducts: builder.query<
      TopProductsResponse,
      {
        limit?: number;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: params => ({
        url: endpoints.dashboard.topProducts.url,
        method: endpoints.dashboard.topProducts.method,
        params,
      }),
    }),
  }),
});

export const {
  useGetDashboardStatsQuery,
  useGetAttentionItemsQuery,
  useGetRecentActivityQuery,
  useGetAnalyticsMetricsQuery,
  useGetTopProductsQuery,
} = dashboardApi;

export const useDashboardSlice = () => {
  useInjectReducer({ key: dashboardApi.reducerPath, reducer: dashboardApi.reducer });
  return {
    ...dashboardApi.endpoints,
    useGetDashboardStatsQuery,
    useGetAttentionItemsQuery,
    useGetRecentActivityQuery,
    useGetAnalyticsMetricsQuery,
    useGetTopProductsQuery,
  };
};
